<template>
  <div class="landing-page">
    <!-- Navigation -->
    <nav class="navbar">
      <div class="container">
        <div class="logo">
          <span class="logo-icon">🎵</span>
          <span class="logo-text">Playlist Transfer</span>
        </div>
        <div class="nav-links">
          <a href="#features">Features</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#platforms">Platforms</a>
          <a href="#download" class="nav-cta">Download</a>
        </div>
      </div>
    </nav>

    <!-- Hero Section -->
    <section class="hero">
      <div class="hero-bg">
        <div class="gradient-orb orb-1"></div>
        <div class="gradient-orb orb-2"></div>
        <div class="gradient-orb orb-3"></div>
      </div>

      <div class="container">
        <div class="hero-content">
          <h1 class="hero-title">
            <span class="title-line">Playlist Transfer</span>
            <span class="title-line accent">Music Shift</span>
          </h1>

          <p class="hero-subtitle">Transfer your favorite playlists, songs and albums to other music platforms</p>

          <p class="hero-description">Effortless song transfer! Transfer playlists, music, and albums across platforms—quick, seamless, reliable. Take your tunes anywhere with Playlist Transfer: Music Shift!</p>

          <div class="download-buttons">
            <a href="#" class="btn-download">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div>
                <span class="btn-label">Download on the</span>
                <span class="btn-store">App Store</span>
              </div>
            </a>

            <a href="#" class="btn-download">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div>
                <span class="btn-label">GET IT ON</span>
                <span class="btn-store">Google Play</span>
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features" id="features">
      <div class="container">
        <h2 class="section-title">What You Can Do with Playlist Transfer</h2>

        <div class="features-grid">
          <div class="feature-card">
            <div class="feature-icon">🎵</div>
            <h3>Cross-Platform Music Transfer</h3>
            <p>Sync your favorite playlists, songs, and albums across various music platforms quickly, eliminating the need for repetitive organization.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🔗</div>
            <h3>Cross-Platform Playlist Sharing</h3>
            <p>Generate unique sharing links to share your playlists with friends easily, breaking down streaming platform barriers.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🎁</div>
            <h3>Two-Way Playlist Acquisition</h3>
            <p>Enter an invitation code to receive content shared by friends, or randomly match with playlists shared by strangers voluntarily.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">✨</div>
            <h3>Personalized Music Discovery</h3>
            <p>Precise recommendations based on your playback preferences, help you discover new music that matches your taste.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">🔄</div>
            <h3>Real-Time Playlist Updates</h3>
            <p>Monitor new song additions to migrated playlists automatically, ensuring content is always up-to-date.</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">💎</div>
            <h3>Superior User Experience</h3>
            <p>A clean and simple interface with intuitive functions makes every use smoother and more convenient.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- How It Works -->
    <section class="how-it-works" id="how-it-works">
      <div class="container">
        <h2 class="section-title">How to Transfer Music</h2>

        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <h3>Select Source Platform</h3>
            <p>Select the source music platform from which you want to transfer music.</p>
          </div>

          <div class="step">
            <div class="step-number">2</div>
            <h3>Login & Authorize</h3>
            <p>Log in to your chosen source music platform account using the web interface.</p>
          </div>

          <div class="step">
            <div class="step-number">3</div>
            <h3>Select Content</h3>
            <p>Select the playlists, songs, and albums you want to transfer.</p>
          </div>

          <div class="step">
            <div class="step-number">4</div>
            <h3>Confirm Transfer</h3>
            <p>Check and confirm the music transfer.</p>
          </div>

          <div class="step">
            <div class="step-number">5</div>
            <h3>Done in Seconds!</h3>
            <p>Let the Playlist Transfer app transfer music you selected in seconds.</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Platforms -->
    <section class="platforms" id="platforms">
      <div class="container">
        <h2 class="section-title">Supported Music Services</h2>
        <div class="platform-grid">
          <div class="platform-card">Spotify</div>
          <div class="platform-card">Apple Music</div>
          <div class="platform-card">YouTube Music</div>
        </div>
      </div>
    </section>

    <!-- Privacy -->
    <section class="privacy">
      <div class="container">
        <div class="privacy-content">
          <div class="privacy-icon">🔒</div>
          <h2>Your Privacy Matters</h2>
          <p class="privacy-intro">We only need to access your public playlist information for the transfer. We will never:</p>
          <ul class="privacy-list">
            <li>Modify or delete any of your content</li>
            <li>Obtain your password or personal information</li>
            <li>Use your data for other purposes</li>
          </ul>
          <p class="privacy-note">The authorization will expire automatically after the playlist transfer is complete.</p>
        </div>
      </div>
    </section>

    <!-- Footer CTA -->
    <section class="footer-cta" id="download">
      <div class="container">
        <h2>Ready to Transfer Your Music?</h2>
        <p>Sync fast, share louder, and keep the good vibes flowing across every app. Ditch the platform limits!</p>
        <div class="download-buttons">
          <a href="#" class="btn-download">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
            <div>
              <span class="btn-label">Download on the</span>
              <span class="btn-store">App Store</span>
            </div>
          </a>
          <a href="#" class="btn-download">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
            </svg>
            <div>
              <span class="btn-label">GET IT ON</span>
              <span class="btn-store">Google Play</span>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <div class="container">
        <p>&copy; 2026 Playlist Transfer: Music Shift. All rights reserved.</p>
        <div class="footer-links">
          <a href="/terms">Terms of Service</a>
          <a href="/privacy">Privacy Policy</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: 'LandingPage'
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

.landing-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #ffffff;
  color: #1a1a2e;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* Navigation */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 1rem 0;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.navbar .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: #3DADFE;
}

.logo-icon {
  font-size: 1.5rem;
}

.nav-links {
  display: flex;
  gap: 2rem;
  align-items: center;
}

.nav-links a {
  color: #4b5563;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s;
}

.nav-links a:hover {
  color: #3DADFE;
}

.nav-cta {
  background: linear-gradient(135deg, #3DADFE 0%, #2b8fd9 100%);
  padding: 0.5rem 1.5rem;
  border-radius: 25px;
  color: white !important;
}

.nav-cta:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(61, 173, 254, 0.4);
}

.section-title {
  font-size: 2.75rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 4rem;
  background: linear-gradient(135deg, #3DADFE 0%, #2b8fd9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

/* Hero */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(180deg, #f0f9ff 0%, #e0f2fe 100%);
  padding-top: 80px;
}

.hero-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.3;
  animation: float 20s infinite ease-in-out;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, #3DADFE, transparent);
  top: -10%;
  left: -10%;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, #60a5fa, transparent);
  bottom: -10%;
  right: -10%;
  animation-delay: -5s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, #93c5fd, transparent);
  top: 50%;
  left: 50%;
  animation-delay: -10s;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0); }
  33% { transform: translate(30px, -30px); }
  66% { transform: translate(-20px, 20px); }
}

.hero-content {
  position: relative;
  z-index: 1;
  text-align: center;
}

.hero-title {
  margin-bottom: 2rem;
}

.title-line {
  display: block;
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.1;
  color: #1e293b;
}

.title-line.accent {
  background: linear-gradient(135deg, #3DADFE 0%, #2b8fd9 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}

.hero-subtitle {
  font-size: 1.5rem;
  color: #475569;
  margin-bottom: 1.5rem;
  font-weight: 500;
}

.hero-description {
  font-size: 1.1rem;
  color: #64748b;
  max-width: 700px;
  margin: 0 auto 3rem;
  line-height: 1.7;
}

/* Download Buttons */
.download-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-download {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.75rem;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(61, 173, 254, 0.2);
  color: #1e293b;
  text-decoration: none;
  border-radius: 14px;
  transition: all 0.3s;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.btn-download:hover {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(61, 173, 254, 0.5);
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(61, 173, 254, 0.2);
}

.btn-download svg {
  width: 24px;
  height: 24px;
}

.btn-download div {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.btn-label {
  font-size: 0.7rem;
  opacity: 0.7;
}

.btn-store {
  font-size: 1.1rem;
  font-weight: 600;
}

/* Features */
.features {
  padding: 6rem 0;
  background: #ffffff;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2.5rem;
  border-radius: 20px;
  transition: all 0.3s;
}

.feature-card:hover {
  background: #ffffff;
  border-color: #3DADFE;
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(61, 173, 254, 0.15);
}

.feature-icon {
  font-size: 3rem;
  margin-bottom: 1.5rem;
}

.feature-card h3 {
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: #1e293b;
  font-weight: 700;
}

.feature-card p {
  color: #64748b;
  line-height: 1.7;
}

/* How It Works */
.how-it-works {
  padding: 6rem 0;
  background: #f8fafc;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
}

.step {
  text-align: center;
}

.step-number {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #3DADFE 0%, #2b8fd9 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  font-weight: 800;
  margin: 0 auto 1.5rem;
  box-shadow: 0 8px 25px rgba(61, 173, 254, 0.3);
}

.step h3 {
  font-size: 1.2rem;
  margin-bottom: 0.75rem;
  color: #1e293b;
}

.step p {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.6;
}

/* Platforms */
.platforms {
  padding: 6rem 0;
  background: #ffffff;
}

.platform-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  max-width: 900px;
  margin: 0 auto;
}

.platform-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  font-size: 1.5rem;
  font-weight: 600;
  color: #3DADFE;
  transition: all 0.3s;
}

.platform-card:hover {
  background: #ffffff;
  border-color: #3DADFE;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(61, 173, 254, 0.15);
}

/* Privacy */
.privacy {
  padding: 6rem 0;
  background: #f8fafc;
}

.privacy-content {
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
}

.privacy-icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
}

.privacy-content h2 {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1.5rem;
}

.privacy-intro {
  font-size: 1.2rem;
  color: #475569;
  margin-bottom: 2rem;
}

.privacy-list {
  list-style: none;
  text-align: left;
  max-width: 500px;
  margin: 0 auto 2rem;
}

.privacy-list li {
  padding: 0.75rem 0 0.75rem 2rem;
  position: relative;
  color: #64748b;
  font-size: 1.05rem;
}

.privacy-list li::before {
  content: "✓";
  position: absolute;
  left: 0;
  color: #10b981;
  font-weight: bold;
  font-size: 1.3rem;
}

.privacy-note {
  font-size: 1rem;
  color: #94a3b8;
  font-style: italic;
}

/* Footer CTA */
.footer-cta {
  padding: 6rem 0;
  background: linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%);
  text-align: center;
  border-top: 1px solid #e2e8f0;
}

.footer-cta h2 {
  font-size: 2.75rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 1rem;
}

.footer-cta p {
  font-size: 1.3rem;
  color: #475569;
  margin-bottom: 2.5rem;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
}

/* Responsive */
@media (max-width: 768px) {
  .nav-links {
    display: none;
  }

  .hero {
    padding-top: 60px;
  }

  .title-line {
    font-size: 2.5rem;
  }

  .hero-subtitle {
    font-size: 1.2rem;
  }

  .section-title {
    font-size: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .steps {
    grid-template-columns: 1fr;
  }

  .platform-grid {
    grid-template-columns: 1fr;
  }

  .footer-cta h2 {
    font-size: 2rem;
  }
}

/* Footer */
.footer {
  background: #f8fafc;
  padding: 2rem 0;
  text-align: center;
  border-top: 1px solid #e2e8f0;
}

.footer p {
  color: #64748b;
  margin-bottom: 1rem;
}

.footer-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.footer-links a {
  color: #3DADFE;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-links a:hover {
  color: #2b8fd9;
}
</style>
